import React from 'react';
import { styled, css, Box, Linkify, Flex, Text } from '@goodrx/matisse';
import { variant } from 'styled-system';

import { useAppContext } from 'app/config/AppContext';
import { MessageProps } from 'app/components/widgets/chat/message-types';
import { MessageSignature } from './MessageSignature';

interface MessageRowProps {
  fromCurrentUser: boolean;
}

export const MessageRow = styled(Flex)<MessageRowProps>`
  ${(p) => css`
    margin-top: ${p.theme.space[2]}px;
    ${p.fromCurrentUser &&
    css`
      justify-content: flex-end;

      & + & {
        margin-top: ${p.theme.space[2]}px;
      }
    `}
  `}
`;

const MessageTitle = styled(Text)`
  font-weight: 600;
  margin-top: 0;
  padding-bottom: ${(p) => p.theme.space[1]}px;
`;

const MessageBody = styled.div<{ fromCurrentUser: boolean; variant: string }>`
  border-top-right-radius: 20px;
  border-top-left-radius: 20px;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 0;
  background-color: ${(p) => p.theme.colors.greyLighter};

  ${variant({
    variants: {
      heydoctor: {
        fontSize: 16,
      },
      gold: {
        fontSize: 15,
      },
    },
  })}

  ${(p) =>
    p.fromCurrentUser &&
    css`
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 20px;
      ${variant({
        variants: {
          heydoctor: {
            fontSize: 16,
            color: 'white',
            backgroundColor: p.theme.colors.primary,
          },
          gold: {
            fontSize: 15,
            color: '#222222',
            backgroundColor: '#DBE6FF',
          },
        },
      })}
    `}
`;

const MessageContent = styled.div<{ baseFontFamily?: string }>`
  padding: 12px;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 750px;

  ul {
    list-style: disc inside;
    margin: 0;
    padding-left: 0;
  }

  ol {
    list-style: decimal inside;
    margin: 0;
    padding-left: 0;
  }

  p {
    font-family: ${(p) => p.baseFontFamily || 'Avenir, sans-serif'};
    margin-bottom: 0;
  }
`;

const DefaultMessageContainer = styled(Box)`
  max-width: 75%;
`;

export interface DefaultMessageProps extends MessageProps {
  fromCurrentUser: boolean;
  isGoldClient?: boolean;
  messageTitle?: string;
}

export const DefaultMessage: React.FC<DefaultMessageProps> = ({
  message,
  message: { body: messageBody },
  children,
  fromCurrentUser,
  messageRef,
  messageTitle,
}) => {
  const {
    isGold,
    which,
    config: {
      Theme: {
        typography: { baseFontFamily },
      },
    },
  } = useAppContext();
  return (
    <MessageRow data-qa="message" fromCurrentUser={fromCurrentUser}>
      <DefaultMessageContainer ref={messageRef}>
        {!isGold && <MessageSignature message={message} fromCurrentUser={fromCurrentUser} />}

        {messageBody && (
          <MessageBody data-qa="message_body" fromCurrentUser={fromCurrentUser} variant={which}>
            <MessageContent baseFontFamily={baseFontFamily}>
              {messageTitle && <MessageTitle as="p">{messageTitle}</MessageTitle>}
              <Linkify source={messageBody} renderers={{ paragraph: 'p' }} />
              {children}
            </MessageContent>
          </MessageBody>
        )}
        {isGold && <MessageSignature message={message} fromCurrentUser={fromCurrentUser} />}
      </DefaultMessageContainer>
    </MessageRow>
  );
};
